import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, AlertTriangle, AlertCircle, Shield, Globe, Database, Clock,
  CheckCircle2, XCircle, Calendar, Activity,
  Server, Tag, MapPin, Hash, FileText, Bug, Zap,
  Key, Lock, Network, Eye, MessageSquare, UserPlus, RotateCcw,
  GitBranch, Wrench, Ticket, Loader2, GitCommit, Code2, User, ExternalLink, Sparkles,
  Copy, GitPullRequest,
} from 'lucide-react';
import { findingsApi, cspmFindingsExtApi } from '../../services/api';
import { apiClient } from '../../services/api/client';
import { useAIAdvisor } from '../../contexts/AIAdvisorContext';
import { useTabFromURL } from '../../hooks/useTabFromURL';
import { SEV_STYLES } from '../../shared/utils/colors';
import FindingGraphPanel from './FindingGraphPanel';
import { fmtDate, fmtDateTime, fmtTime } from '../../utils/date';
import type { Resource, Vulnerability } from '../../shared/types';
import { useDialog } from '../Common/DialogProvider';


// ─── Copy-to-clipboard button (reusable, no external dep) ──────────────────────
const CopyButton: React.FC<{ text: string; label?: string }> = ({ text, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — no-op */ }
  }, [text]);
  return (
    <button
      onClick={onCopy}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border border-wiz-border text-gray-400 hover:text-white hover:border-gray-500 transition-colors"
    >
      {copied ? <CheckCircle2 className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : label}
    </button>
  );
};


// ─── Types ────────────────────────────────────────────────────────────────────

interface Finding {
  id: string;
  title: string;
  description?: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  status: string;
  source_module: string;
  rule_title?: string;
  rule_name?: string;
  resource_id?: string;
  resource_name?: string;
  resource_type?: string;
  provider?: string;
  region?: string;
  detected_at?: string;
  compliance_frameworks?: Record<string, string[]>;
  // Wiz-parity signal fusion inputs. The backend stores them on
  // csp_core.findings; the Risk Signal Breakdown panel reads them to
  // explain why `effective_risk` has its current value.
  effective_risk?: number;
  epss_score?: number;
  blast_radius_score?: number;
  is_internet_facing?: boolean;
  has_sensitive_data?: boolean;
  has_admin_access?: boolean;
  exploit_available?: boolean;
  privilege_escalation_possible?: boolean;
  lateral_movement_possible?: boolean;
  // Wiz-parity MITRE ATT&CK enrichment — populated by finding-normalizer +
  // mig 213 trg_enrich_finding from the rule's metadata.
  mitre_tactics?: string[] | string;
  mitre_techniques?: string[] | string;
}

interface FindingDetailProps {
  finding: Finding;
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// SEV_STYLES imported from ../../shared/utils/colors

const SEV_SCORE: Record<string, number> = {
  critical: 95, high: 75, medium: 50, low: 25, info: 10,
};

const MODULE_COLORS: Record<string, string> = {
  cspm: 'bg-blue-500/15 text-blue-300 border-blue-500/25',
  cwpp: 'bg-purple-500/15 text-purple-300 border-purple-500/25',
  ciem: 'bg-pink-500/15 text-pink-300 border-pink-500/25',
  dspm: 'bg-teal-500/15 text-teal-300 border-teal-500/25',
  cdr:  'bg-red-500/15 text-red-300 border-red-500/25',
  easm: 'bg-orange-500/15 text-orange-300 border-orange-500/25',
};

const PROVIDER_FLAGS: Record<string, string> = { aws: '🟠', azure: '🔵', gcp: '🟢', oci: '🔴' };

const RESOURCE_TYPE_ICONS: Record<string, React.FC<any>> = {
  ec2:           Server,
  ec2_instance:  Server,
  s3_bucket:     Database,
  rds_instance:  Database,
  iam_role:      Key,
  iam_user:      Key,
  lambda_function: Zap,
  k8s_pod:       Globe,
  vpc:           Network,
  security_group: Shield,
  kms_key:       Lock,
  secrets_manager: Lock,
  azure_blob_container: Database,
  virtual_machine: Server,
};

function timeAgo(iso?: string) {
  if (!iso) return '—';
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

// The drawer is now opened from every pillar, but only findings that live in
// csp_core.findings resolve the /issues/findings/{id}/... enrichment tabs. For
// pillar-local findings those endpoints 404 — render that as a neutral "not
// available for this source" note rather than an alarming red error string.
function TabError({ error }: { error: string }) {
  const is404 = /\b404\b/.test(error);
  return (
    <div className={`text-xs ${is404 ? 'text-gray-500' : 'text-red-300'}`}>
      {is404
        ? 'Graph/enrichment data is only available for findings tracked in the unified findings store. This finding comes from a pillar-local source.'
        : error}
    </div>
  );
}

// Derive remediation steps from finding type/title
function deriveSteps(finding: Finding): string[] {
  const t = (finding.title || finding.rule_title || finding.rule_name || '').toLowerCase();
  const m = finding.source_module || '';

  if (t.includes('cve') || t.includes('log4') || m === 'cwpp')
    return [
      'Identify the affected software component and version.',
      'Apply the vendor security patch or upgrade to a non-vulnerable version.',
      'Verify the fix by re-scanning the resource.',
      'Monitor for exploitation attempts in runtime logs.',
      'Update your container images / AMIs to bake in the fix.',
    ];
  if (t.includes('s3') || t.includes('public') || t.includes('bucket'))
    return [
      'Remove public access from the S3 bucket via the AWS Console or CLI.',
      'Enable "Block Public Access" settings at the account level.',
      'Review and tighten the bucket policy and ACL.',
      'Enable S3 server access logging.',
      'If PII/PCI data exists, enable server-side encryption.',
    ];
  if (t.includes('iam') || t.includes('role') || t.includes('wildcard') || m === 'ciem')
    return [
      'Review the current IAM policy and identify overly permissive statements.',
      'Replace wildcard (*) permissions with specific resource-level actions.',
      'Remove permissions that have not been used in 90 days (review via IAM Access Analyzer).',
      'Enforce MFA for any human identities with admin access.',
      'Set up permission boundaries to limit the blast radius.',
    ];
  if (t.includes('mfa'))
    return [
      'Navigate to IAM → Users → Select the user.',
      'Under "Security credentials", enable a virtual MFA device.',
      'Enforce MFA via an IAM policy condition: aws:MultiFactorAuthPresent.',
      'Audit other users for MFA compliance.',
    ];
  if (t.includes('encrypt') || t.includes('kms'))
    return [
      'Enable server-side encryption on the resource using AWS KMS.',
      'Select a customer-managed key (CMK) rather than AWS-managed key for control.',
      'Enable automatic KMS key rotation (annual).',
      'Review who has kms:Decrypt permissions on the key.',
    ];
  if (t.includes('vpc') || t.includes('flow log'))
    return [
      'Enable VPC Flow Logs via the AWS Console → VPC → Flow logs.',
      'Send logs to CloudWatch Logs or S3 for analysis.',
      'Set up CloudWatch alarms for anomalous traffic patterns.',
      'Review Security Group rules for overly permissive ingress/egress.',
    ];
  if (t.includes('rds') || t.includes('database'))
    return [
      'Disable public accessibility on the RDS instance (Multi-AZ or VPC-only).',
      'Rotate the database credentials immediately.',
      'Enable RDS enhanced monitoring and audit logging.',
      'Restrict Security Group ingress to known application subnets only.',
    ];
  return [
    'Review the affected resource in your cloud console.',
    'Apply the recommended security configuration change.',
    'Verify the fix is applied and re-run the scan.',
    'Update IaC templates (Terraform/CloudFormation) to prevent recurrence.',
  ];
}

// Normalize BE-supplied remediation `steps` (string, numbered blob, or array)
// into a clean list. Returns [] when nothing usable is present.
function normalizeBackendSteps(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map(s => String(s).trim()).filter(Boolean);
  }
  if (typeof raw === 'string' && raw.trim()) {
    return raw
      .split(/\r?\n|(?<=\.)\s+(?=\d+[.)]\s)/) // newlines, or "1. .. 2. .." runs
      .map(s => s.replace(/^\s*(?:\d+[.)]|[-*•])\s*/, '').trim()) // strip bullet/number prefix
      .filter(Boolean);
  }
  return [];
}

// ─── Section wrapper ───────────────────────────────────────────────────────────

const Section: React.FC<{
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  /** Optional count/label badge shown after the title (e.g. "3 scanners"). */
  tag?: string;
}> = ({ title, icon, children, tag }) => (
  <div>
    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-800">
      {icon && <span className="text-gray-400 w-4 h-4 [&>svg]:w-full [&>svg]:h-full">{icon}</span>}
      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">{title}</h3>
      {tag && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/25 font-mono">
          {tag}
        </span>
      )}
    </div>
    {children}
  </div>
);

// ─── Main component ────────────────────────────────────────────────────────────

const FINDING_TAB_IDS = ['details', 'graph', 'code', 'notes', 'remediation', 'related', 'history'] as const;
type FindingTabId = typeof FINDING_TAB_IDS[number];

const FindingDetail: React.FC<FindingDetailProps> = ({ finding, onClose }) => {
  const { prompt } = useDialog();
  const navigate = useNavigate();
  const { askAbout } = useAIAdvisor();

  // Item #4: hand the grounded advisor this exact finding. We pass a snapshot
  // of fields already loaded here (real data) so the advisor is grounded even
  // when the id is pillar-local and the backend's live-fetch would 404.
  const askAI = useCallback(() => {
    askAbout({
      kind: 'finding',
      id: String(finding.id),
      label: finding.title,
      snapshot: {
        title: finding.title,
        severity: finding.severity,
        status: finding.status,
        source_module: finding.source_module,
        rule_id: (finding as any).rule_id,
        resource_name: (finding as any).resource_name,
        resource_type: (finding as any).resource_type,
        resource_arn: (finding as any).resource_arn,
        cloud_provider: (finding as any).cloud_provider,
        description: (finding as any).description,
        remediation: (finding as any).remediation,
      },
    });
  }, [askAbout, finding]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Active tab
  const [activeTab, setActiveTab] = useTabFromURL<FindingTabId>(FINDING_TAB_IDS, 'details', 'dtab');

  // Change-history tab — Wiz-parity "when did this finding's severity flip?"
  // Backed by csp_core.finding_history (populated by the diff-driven hook
  // in finding-normalizer — only meaningful changes land here, not every
  // re-scan). Mirrors the Related tab's lazy-fetch pattern.
  interface HistoryEvent {
    id: number;
    change_type: string;
    prev_status?: string | null;
    new_status?: string | null;
    prev_severity?: string | null;
    new_severity?: string | null;
    prev_effective_risk?: number | null;
    new_effective_risk?: number | null;
    changed_by?: string;
    changed_at?: string;
    note?: string | null;
  }
  const [history, setHistory] = useState<{
    events?: HistoryEvent[];
    loading?: boolean;
    error?: string;
  } | null>(null);

  // M1 — IaC remediation payload, fetched on Remediation tab open.
  interface RemediationData {
    steps?: string | string[];
    description?: string;
    cli?: string;
    terraform?: string;
    iac_source?: 'rule_catalog' | 'generator' | null;
    auto_remediable?: boolean;
  }
  const [remediationData, setRemediationData] = useState<{
    data?: RemediationData;
    loading?: boolean;
    error?: string;
  } | null>(null);

  // #6 — "Open as PR": creates a REAL GitHub PR with the IaC fix via
  // remediation-service. Requires the user's repo + token, so it's an explicit
  // inline form, not a fabricated one-click.
  const [prForm, setPrForm] = useState<{ open: boolean; repo_url: string; branch: string; token: string; format: string }>({
    open: false, repo_url: '', branch: 'main', token: '', format: 'terraform',
  });
  const [prState, setPrState] = useState<{ loading?: boolean; url?: string; number?: number; error?: string } | null>(null);

  const handleOpenPr = useCallback(async () => {
    if (!prForm.repo_url.trim() || !prForm.token.trim()) return;
    setPrState({ loading: true });
    const { data, error, status } = await apiClient.request<any>('/api/v1/remediation/fix-in-code', {
      method: 'POST',
      body: {
        finding_id: finding.id,
        repo_url: prForm.repo_url.trim(),
        branch: prForm.branch.trim() || 'main',
        github_token: prForm.token.trim(),
        iac_format: prForm.format,
      },
      timeoutMs: 30000,
    });
    if (error || !data) {
      setPrState({ error: `PR creation failed (HTTP ${status})` });
      return;
    }
    setPrState({ url: (data as any).pr_url, number: (data as any).pr_number });
    setPrForm(f => ({ ...f, open: false, token: '' }));  // drop the token from state after use
  }, [prForm, finding.id]);

  // Lazy per-tab fetch latches.
  //
  // These MUST be refs, not the fetched state itself. The original guards read
  // the state (`impact?.data !== undefined`) while listing that same state in
  // the dependency array, and set a `{ loading: true }` placeholder before the
  // await. That placeholder re-renders, the dep identity changes, the effect
  // re-runs, and the guard still sees `data === undefined` — so it fires again.
  // Worse, every re-run's cleanup sets the previous run's `cancelled = true`,
  // so no in-flight response is ever allowed to store `data` and latch the
  // guard. Result: an unbounded request storm on a perfectly healthy endpoint
  // (measured 4,366 requests in 6s on the Graph tab, until Chrome returned
  // net::ERR_INSUFFICIENT_RESOURCES and the socket exhaustion knocked the
  // session's own profile fetch offline).
  //
  // A ref latches synchronously and does not re-render, so the effect depends
  // only on what should actually retrigger it: the active tab and the finding.
  const impactReqRef = useRef<string | null>(null);
  const remediationReqRef = useRef<string | null>(null);
  const historyReqRef = useRef<string | null>(null);
  const codeOriginReqRef = useRef<string | null>(null);
  const relatedReqRef = useRef<string | null>(null);

  // M4 — Impact (downstream) data, fetched on Graph tab open.
  interface ImpactData {
    downstream_count?: number;
    crown_jewels_in_path?: number;
    downstream_sample?: Array<{ name?: string; type?: string; id?: string; is_crown_jewel?: boolean }>;
    source?: string;
    warning?: string;
  }
  const [impact, setImpact] = useState<{
    data?: ImpactData;
    loading?: boolean;
    error?: string;
  } | null>(null);

  useEffect(() => {
    if (activeTab !== 'graph') return;
    if (impactReqRef.current === finding.id) return;
    impactReqRef.current = finding.id;
    let cancelled = false;
    (async () => {
      setImpact({ loading: true });
      try {
        const { data: d, error, status } = await apiClient.get<any>(`/api/v1/issues/findings/${encodeURIComponent(finding.id)}/impact?max_hops=3`);
        if (error || !d) {
          if (!cancelled) setImpact({ error: `HTTP ${status}` });
          return;
        }
        if (!cancelled) setImpact({ data: d });
      } catch (e: any) {
        if (!cancelled) setImpact({ error: e?.message || String(e) });
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, finding.id]);

  useEffect(() => {
    if (activeTab !== 'remediation') return;
    if (remediationReqRef.current === finding.id) return;
    remediationReqRef.current = finding.id;
    let cancelled = false;
    (async () => {
      setRemediationData({ loading: true });
      try {
        const { data: d, error, status } = await apiClient.get<any>(`/api/v1/issues/findings/${encodeURIComponent(finding.id)}/remediation`);
        if (error || !d) {
          if (!cancelled) setRemediationData({ error: `HTTP ${status}` });
          return;
        }
        if (!cancelled) setRemediationData({ data: d });
      } catch (e: any) {
        if (!cancelled) setRemediationData({ error: e?.message || String(e) });
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, finding.id]);

  useEffect(() => {
    if (activeTab !== 'history') return;
    if (historyReqRef.current === finding.id) return;
    historyReqRef.current = finding.id;
    let cancelled = false;
    (async () => {
      setHistory({ loading: true });
      try {
        const { data: d, error, status } = await apiClient.get<any>(`/api/v1/issues/findings/${encodeURIComponent(finding.id)}/history`);
        if (error || !d) {
          if (!cancelled) setHistory({ error: `HTTP ${status}` });
          return;
        }
        if (!cancelled) setHistory({ events: (d as any).events || [] });
      } catch (e: any) {
        if (!cancelled) setHistory({ error: e?.message || String(e) });
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, finding.id]);

  // Code-to-cloud trace-back — Wiz-parity "which IaC line / PR / developer
  // introduced this?". Two real DB-backed sources, both keyed on the finding's
  // cloud resource id:
  //   • /api/v1/code-to-cloud/chains  → commit author (developer), PR, pipeline,
  //     risk origin (csp_core.code_to_cloud_chains)
  //   • /api/v1/iac/mappings          → exact file_path:line_number + permalink
  //     (csp_iac.terraform_resources → repositories)
  // Fetched only when the Code tab opens and the finding carries a resource id.
  interface CommitBlob {
    sha?: string; short_sha?: string; author?: string; author_email?: string;
    message?: string; branch?: string; repo_url?: string;
    pr_number?: number; pr_title?: string; pr_url?: string;
  }
  interface ChainData {
    commit?: CommitBlob | null;
    pipeline_run?: { name?: string; triggered_by?: string; status?: string } | null;
    image_build?: { repository?: string; image_tag?: string; digest?: string } | null;
    introduced_by?: string | null;
    introduced_at?: string | null;
    risk_origin?: string;
    chain_confidence?: number;
  }
  interface IacMapping {
    file_path?: string; line_number?: number; iac_type?: string; iac_name?: string;
    commit_sha?: string; commit_author?: string; commit_date?: string;
    repo_url?: string; repo_name?: string; permalink?: string | null;
  }
  const [codeOrigin, setCodeOrigin] = useState<{
    chain?: ChainData | null;
    mapping?: IacMapping | null;
    loading?: boolean;
    error?: string;
  } | null>(null);

  useEffect(() => {
    if (activeTab !== 'code') return;
    const rid = finding.resource_id;
    if (codeOriginReqRef.current === (rid || '')) return;
    codeOriginReqRef.current = rid || '';
    if (!rid) { setCodeOrigin({ chain: null, mapping: null }); return; }
    let cancelled = false;
    (async () => {
      setCodeOrigin({ loading: true });
      try {
        const [chainsResp, mapResp] = await Promise.all([
          apiClient.get<any>(`/api/v1/code-to-cloud/chains?resource_id=${encodeURIComponent(rid)}&limit=1`),
          apiClient.get<any>(`/api/v1/iac/mappings?cloud_resource_id=${encodeURIComponent(rid)}&limit=1`),
        ]);
        if (cancelled) return;
        const chain = (chainsResp.data?.chains && chainsResp.data.chains[0]) || null;
        const mapping = (mapResp.data?.mappings && mapResp.data.mappings[0]) || null;
        setCodeOrigin({ chain, mapping });
      } catch (e: any) {
        if (!cancelled) setCodeOrigin({ error: e?.message || String(e), chain: null, mapping: null });
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, finding.resource_id]);

  // Related findings by fingerprint — Wiz-parity "this CVE also detected by
  // N other scanners" panel. Lazy-fetched on tab activation so closed tabs
  // don't waste a round-trip per finding drawer open.
  interface RelatedFinding {
    id: string;
    source_module: string;
    severity: string;
    status: string;
    effective_risk?: number;
    title: string;
    first_seen_at?: string;
  }
  const [related, setRelated] = useState<{
    fingerprint?: string | null;
    detected_by_count?: number;
    total_related?: number;
    related?: RelatedFinding[];
    loading?: boolean;
    error?: string;
  } | null>(null);

  useEffect(() => {
    // Only fetch when the user actually opens the Related tab (saves RTT
    // on drawer-open when most users never look at siblings).
    if (activeTab !== 'related') return;
    if (relatedReqRef.current === finding.id) return;
    relatedReqRef.current = finding.id;
    let cancelled = false;
    (async () => {
      setRelated({ loading: true });
      try {
        const { data: dataResp, error, status } = await apiClient.get<any>(`/api/v1/issues/findings/${encodeURIComponent(finding.id)}/related`);
        if (error || !dataResp) {
          if (!cancelled) setRelated({ error: `HTTP ${status}` });
          return;
        }
        const d = dataResp as any;
        if (!cancelled) setRelated({
          fingerprint: d.fingerprint,
          detected_by_count: d.detected_by_count,
          total_related: d.total_related,
          related: d.related || [],
        });
      } catch (e: any) {
        if (!cancelled) setRelated({ error: e?.message || String(e) });
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, finding.id]);

  // Notes
  const [notes, setNotes] = useState<any[]>([]);
  const [newNote, setNewNote] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const loadNotes = useCallback(() => {
    cspmFindingsExtApi.getIssueNotes(finding.id).then((r: any) => {
      setNotes(Array.isArray(r) ? r : r?.notes ?? []);
    }).catch(() => setNotes([]));
  }, [finding.id]);
  useEffect(() => { loadNotes(); }, [loadNotes]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setNoteError(null);
    try {
      // BE returns {message, notes_count}; re-fetch to render the persisted note.
      await cspmFindingsExtApi.addIssueNote(finding.id, { content: newNote });
      setNewNote('');
      loadNotes();
    } catch (e: any) {
      setNoteError(e?.message || 'Failed to add note');
    }
  };

  // Actions
  const [suppressing, setSuppressing] = useState(false);
  const [resolving, setResolving] = useState(false);

  // Loop-closing actions — the Remediation tab was display-only; these wire it
  // to the real backend so a user can actually trigger a fix or open a ticket
  // from the drawer. Both surface the backend's HONEST outcome verbatim
  // (dry_run / approval_required / no_template / executed) rather than faking
  // success.
  const [remediating, setRemediating] = useState(false);
  const [remediateResult, setRemediateResult] = useState<{ ok?: boolean; message?: string } | null>(null);
  const [ticketing, setTicketing] = useState(false);
  const [ticketResult, setTicketResult] = useState<{ ok?: boolean; message?: string; url?: string } | null>(null);

  const handleRemediate = async () => {
    setRemediating(true);
    setRemediateResult(null);
    try {
      // POST /api/v1/cspm/findings/{id}/remediate → delegates to the
      // remediation-service and returns {success, message, remediation_status}.
      const r: any = await findingsApi.remediate(finding.id, { auto: true });
      setRemediateResult({
        ok: !!r?.success,
        message: r?.message || `Status: ${r?.remediation_status?.status ?? 'unknown'}`,
      });
    } catch (e: any) {
      setRemediateResult({ ok: false, message: e?.message || 'Remediation request failed' });
    } finally {
      setRemediating(false);
    }
  };

  const handleCreateTicket = async () => {
    setTicketing(true);
    setTicketResult(null);
    try {
      // Same Jira endpoint the Findings list page uses (finding_id-keyed).
      const { data, error } = await apiClient.post<any>('/api/v1/integrations/jira/create-ticket', {
        finding_id: finding.id,
        title: finding.title,
        description: finding.description || '',
        severity: finding.severity,
        affected_resources: finding.resource_name ? [finding.resource_name] : [],
        labels: [finding.source_module],
      });
      if (error) {
        setTicketResult({ ok: false, message: error || 'Failed to create ticket' });
        return;
      }
      const t = (data as any)?.ticket;
      const url = t?.browse_url;
      setTicketResult({
        ok: true,
        message: t?.key ? `Ticket ${t.key} created` : 'Ticket created',
        url,
      });
      if (url) window.open(url, '_blank');
    } catch (e: any) {
      setTicketResult({ ok: false, message: e?.message || 'Ticket creation failed' });
    } finally {
      setTicketing(false);
    }
  };

  const [resolveError, setResolveError] = useState<string | null>(null);
  const handleResolve = async () => {
    setResolving(true);
    setResolveError(null);
    try {
      // PATCH /api/v1/cspm/findings/{id} {status:'resolved'} — now DB-durable.
      await findingsApi.updateStatus(finding.id, 'resolved' as any);
      onClose();
    } catch (e: any) {
      // Surface the failure instead of silently closing as if it succeeded.
      setResolveError(e?.message || 'Failed to mark resolved');
    }
    finally { setResolving(false); }
  };

  const handleSuppress = async () => {
    const v = await prompt({
      title: 'Suppress this finding',
      message: 'Suppressed findings stay in the estate but drop out of the default queue.',
      fields: [{ name: 'reason', label: 'Suppression reason', type: 'textarea', required: true }],
      submitLabel: 'Suppress',
    });
    if (!v) return;
    const reason = v.reason;
    setSuppressing(true);
    try {
      await findingsApi.suppress(finding.id, { reason });
      onClose();
    } catch { /* ignore */ }
    finally { setSuppressing(false); }
  };

  const sev = SEV_STYLES[finding.severity] ?? SEV_STYLES.info;
  // Prefer the real signal-fused effective_risk for the headline gauge; only
  // fall back to the coarse severity→score map when the backend hasn't scored
  // this finding yet. Both are 0–100.
  const hasEffectiveRisk = typeof finding.effective_risk === 'number' && finding.effective_risk > 0;
  const score = hasEffectiveRisk
    ? Math.round(finding.effective_risk as number)
    : (SEV_SCORE[finding.severity] ?? 10);
  const ResourceIcon = RESOURCE_TYPE_ICONS[finding.resource_type ?? ''] ?? Server;
  const steps = deriveSteps(finding);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} />

      {/* Slide-over */}
      <div data-testid="finding-detail-panel" className="fixed right-0 top-0 h-full w-full max-w-xl bg-wiz-darker border-l border-gray-800 z-50
        flex flex-col shadow-2xl overflow-hidden animate-in slide-in-from-right duration-200">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex-shrink-0 border-b border-gray-800 p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex flex-wrap items-center gap-2">
              <span data-testid="finding-detail-severity" className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${sev.pill}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${sev.dot}`} />
                {finding.severity.toUpperCase()}
              </span>
              <span data-testid="finding-detail-module" className={`px-2 py-0.5 rounded text-xs font-semibold uppercase border ${MODULE_COLORS[finding.source_module] ?? 'bg-gray-700 text-gray-400 border-gray-600'}`}>
                {finding.source_module.replace('_', '-')}
              </span>
              <span data-testid="finding-detail-status" className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                finding.status === 'open' ? 'bg-red-500/15 text-red-400' : 'bg-gray-700 text-gray-400'
              }`}>
                {finding.status}
              </span>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                data-testid="finding-detail-ask-ai"
                onClick={askAI}
                title="Ask the AI advisor about this finding"
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-wiz-purple border border-wiz-purple/30 bg-wiz-purple/10 hover:bg-wiz-purple/20 transition-colors"
              >
                <Sparkles className="w-3 h-3" />
                Ask AI
              </button>
              <button
                data-testid="finding-detail-close"
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-gray-800 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <h2 data-testid="finding-detail-title" className="text-base font-semibold text-white leading-snug mb-2">{finding.title}</h2>
          {finding.id && <span data-testid="finding-detail-id" className="hidden">{finding.id}</span>}

          <div className="flex items-center gap-3 text-xs text-gray-500">
            {finding.detected_at && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Detected {timeAgo(finding.detected_at)}
              </span>
            )}
            <span className="text-gray-700">·</span>
            <span className="font-mono text-gray-600 text-xs">{finding.id}</span>
          </div>
        </div>

        {/* ── Tab bar ──────────────────────────────────────────────────── */}
        <div className="flex-shrink-0 border-b border-gray-800 px-5">
          <div className="flex items-center gap-0 -mb-px">
            {([
              { id: 'details',     label: 'Details',     icon: <FileText className="w-3.5 h-3.5"/> },
              { id: 'graph',       label: 'Graph',       icon: <GitBranch className="w-3.5 h-3.5"/> },
              { id: 'code',        label: 'Code',        icon: <Code2 className="w-3.5 h-3.5"/> },
              { id: 'related',     label: 'Related',     icon: <Hash className="w-3.5 h-3.5"/> },
              { id: 'history',     label: 'History',     icon: <Clock className="w-3.5 h-3.5"/> },
              { id: 'remediation', label: 'Remediation', icon: <Shield className="w-3.5 h-3.5"/> },
              { id: 'notes',       label: 'Notes',       icon: <MessageSquare className="w-3.5 h-3.5"/> },
            ] as const).map(t => (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium border-b-2 transition-colors ${
                  activeTab === t.id
                    ? 'border-purple-500 text-purple-300'
                    : 'border-transparent text-gray-500 hover:text-gray-300'
                }`}
              >
                {t.icon}{t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Scrollable body ────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">

          {/* ── GRAPH TAB ── */}
          {activeTab === 'graph' && (
            <div>
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-white mb-1">Related Resources Graph</h3>
                <p className="text-xs text-gray-500">
                  Neo4j-backed relationship graph showing how <span className="text-gray-300">{finding.resource_name || 'this resource'}</span> connects to other cloud assets, identities, and policies.
                </p>
              </div>
              <FindingGraphPanel findingId={finding.id} centerId={finding.resource_id} resourceName={finding.resource_name} />

              {/* M4 — Impact Analysis. Wiz-parity "what breaks if I fix this?"
                  Powered by the existing /blast-radius graph endpoint; we just
                  adapt it per finding. */}
              <div className="mt-4">
                <Section
                  title="Impact Analysis"
                  icon={<Network />}
                  tag={typeof impact?.data?.downstream_count === 'number'
                    ? `${impact.data.downstream_count} downstream`
                    : undefined}
                >
                  <p className="text-[11px] text-gray-500 mb-2">
                    Resources reachable from this asset within 3 hops. Fixing
                    or removing the finding's resource may affect these
                    downstream dependencies.
                  </p>
                  {impact?.loading && (
                    <div className="text-xs text-gray-500">Computing impact…</div>
                  )}
                  {impact?.error && <TabError error={impact.error} />}
                  {impact?.data && (
                    <>
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <div className="px-3 py-2 bg-black/20 border border-gray-800 rounded text-xs">
                          <div className="text-gray-500">Reachable resources</div>
                          <div className="text-lg font-semibold text-purple-300">
                            {impact.data.downstream_count ?? 0}
                          </div>
                        </div>
                        <div className="px-3 py-2 bg-black/20 border border-gray-800 rounded text-xs">
                          <div className="text-gray-500">Crown jewels in path</div>
                          <div className={`text-lg font-semibold ${
                            (impact.data.crown_jewels_in_path ?? 0) > 0
                              ? 'text-red-300'
                              : 'text-gray-300'
                          }`}>
                            {impact.data.crown_jewels_in_path ?? 0}
                          </div>
                        </div>
                      </div>
                      {impact.data.warning && (
                        <div className="text-[11px] text-yellow-300 mb-2">
                          ⚠ {impact.data.warning}
                        </div>
                      )}
                      {impact.data.downstream_sample && impact.data.downstream_sample.length > 0 && (
                        <>
                          <div className="text-[11px] text-gray-400 mb-1.5">
                            Sample (top {impact.data.downstream_sample.length}):
                          </div>
                          <div className="space-y-1 max-h-[180px] overflow-y-auto pr-1">
                            {impact.data.downstream_sample.map((d, i) => (
                              <div
                                key={d.id || i}
                                className="px-2 py-1 bg-black/20 border border-gray-800 rounded text-[11px] font-mono text-gray-300 flex items-center gap-2"
                              >
                                {d.is_crown_jewel && (
                                  <span className="text-red-400">👑</span>
                                )}
                                <span className="text-gray-500 shrink-0">
                                  {d.type || 'resource'}
                                </span>
                                <span className="text-gray-400">·</span>
                                <span className="truncate">{d.name || d.id || '—'}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </Section>
              </div>
            </div>
          )}

          {/* ── CODE (code-to-cloud trace-back) TAB ── */}
          {activeTab === 'code' && (() => {
            const chain = codeOrigin?.chain;
            const mapping = codeOrigin?.mapping;
            const commit = chain?.commit;
            // Prefer the IaC mapping's precise commit metadata; fall back to the
            // chain's commit blob.
            const developer = mapping?.commit_author || chain?.introduced_by || commit?.author || commit?.author_email;
            const repoUrl = mapping?.repo_url || commit?.repo_url;
            const commitSha = mapping?.commit_sha || commit?.sha;
            const shortSha = commit?.short_sha || (commitSha ? commitSha.slice(0, 8) : undefined);
            const commitLink = repoUrl && commitSha
              ? `${repoUrl.replace(/\.git$/, '')}/commit/${commitSha}`
              : undefined;
            const hasAny = Boolean(chain || mapping);
            return (
              <div>
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-white mb-1">Code-to-Cloud Trace-Back</h3>
                  <p className="text-xs text-gray-500">
                    Where this resource was defined in Infrastructure-as-Code — the exact file/line,
                    the commit &amp; developer that introduced it, and the pull request.
                  </p>
                </div>

                {codeOrigin?.loading && (
                  <div className="text-xs text-gray-500">Tracing to source…</div>
                )}
                {codeOrigin?.error && <TabError error={codeOrigin.error} />}

                {codeOrigin && !codeOrigin.loading && !codeOrigin.error && !hasAny && (
                  <div className="text-xs text-gray-500 bg-black/20 border border-gray-800 rounded-lg p-4">
                    No IaC source is mapped to this resource yet.
                    {finding.resource_id
                      ? ' Ingest or scan the defining repository in Code Security to enable trace-back.'
                      : ' This finding has no associated cloud resource id to trace from.'}
                  </div>
                )}

                {hasAny && (
                  <div className="space-y-4">
                    {/* Exact IaC location + permalink */}
                    {mapping && (
                      <Section title="IaC Source" icon={<FileText />} tag={mapping.iac_type || undefined}>
                        <div className="p-3 bg-black/20 border border-gray-800 rounded-lg space-y-2 text-xs">
                          <div className="flex items-center gap-2">
                            <FileText className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                            <span className="font-mono text-gray-200 truncate" title={mapping.file_path}>
                              {mapping.file_path || '—'}
                              {mapping.line_number ? <span className="text-purple-300">:{mapping.line_number}</span> : null}
                            </span>
                          </div>
                          {mapping.iac_name && (
                            <div className="flex items-center gap-2">
                              <Tag className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                              <span className="font-mono text-gray-300">{mapping.iac_type} · {mapping.iac_name}</span>
                            </div>
                          )}
                          {mapping.permalink && (
                            <a href={mapping.permalink} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 mt-1 px-2.5 py-1 rounded-lg bg-wiz-purple/20 text-wiz-purple border border-wiz-purple/30 hover:bg-wiz-purple/30 transition-colors">
                              <ExternalLink className="w-3 h-3" /> View source line
                            </a>
                          )}
                        </div>
                      </Section>
                    )}

                    {/* Developer + commit + PR */}
                    {(developer || commitSha || commit?.pr_url || repoUrl) && (
                      <Section title="Introduced By" icon={<GitCommit />}
                        tag={chain?.risk_origin && chain.risk_origin !== 'unknown' ? `origin: ${chain.risk_origin}` : undefined}>
                        <div className="p-3 bg-black/20 border border-gray-800 rounded-lg space-y-2 text-xs">
                          {developer && (
                            <div className="flex items-center gap-2">
                              <User className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                              <span className="text-gray-500 w-16 shrink-0">Developer</span>
                              <span className="text-gray-200 truncate">{developer}</span>
                            </div>
                          )}
                          {repoUrl && (
                            <div className="flex items-center gap-2">
                              <GitBranch className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                              <span className="text-gray-500 w-16 shrink-0">Repo</span>
                              <a href={repoUrl.replace(/\.git$/, '')} target="_blank" rel="noopener noreferrer"
                                className="text-blue-300 hover:underline truncate">
                                {mapping?.repo_name || repoUrl.replace(/^https?:\/\//, '').replace(/\.git$/, '')}
                              </a>
                              {commit?.branch && <span className="text-gray-600 font-mono">@{commit.branch}</span>}
                            </div>
                          )}
                          {commitSha && (
                            <div className="flex items-center gap-2">
                              <GitCommit className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                              <span className="text-gray-500 w-16 shrink-0">Commit</span>
                              {commitLink ? (
                                <a href={commitLink} target="_blank" rel="noopener noreferrer"
                                  className="font-mono text-blue-300 hover:underline">{shortSha}</a>
                              ) : (
                                <span className="font-mono text-gray-300">{shortSha}</span>
                              )}
                              {mapping?.commit_date && <span className="text-gray-600">· {fmtDate(mapping.commit_date)}</span>}
                            </div>
                          )}
                          {commit?.message && (
                            <div className="text-gray-400 italic border-l-2 border-gray-800 pl-2 truncate" title={commit.message}>
                              {commit.message}
                            </div>
                          )}
                          {commit?.pr_url ? (
                            <a href={commit.pr_url} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 mt-1 px-2.5 py-1 rounded-lg bg-green-500/10 text-green-300 border border-green-500/25 hover:bg-green-500/20 transition-colors">
                              <ExternalLink className="w-3 h-3" />
                              {commit.pr_number ? `PR #${commit.pr_number}` : 'Pull Request'}
                              {commit.pr_title ? <span className="text-green-400/70 truncate max-w-[180px]">— {commit.pr_title}</span> : null}
                            </a>
                          ) : commit?.pr_number ? (
                            <div className="text-gray-400">PR #{commit.pr_number}{commit.pr_title ? ` — ${commit.pr_title}` : ''}</div>
                          ) : null}
                        </div>
                      </Section>
                    )}

                    {/* CI/CD + image (only when the chain carries them) */}
                    {(chain?.pipeline_run || chain?.image_build) && (
                      <Section title="Delivery" icon={<Activity />}>
                        <div className="p-3 bg-black/20 border border-gray-800 rounded-lg space-y-2 text-xs">
                          {chain?.pipeline_run?.name && (
                            <div className="flex items-center gap-2">
                              <Activity className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                              <span className="text-gray-500 w-16 shrink-0">Pipeline</span>
                              <span className="text-gray-200 truncate">{chain.pipeline_run.name}
                                {chain.pipeline_run.triggered_by ? <span className="text-gray-500"> · {chain.pipeline_run.triggered_by}</span> : null}
                              </span>
                            </div>
                          )}
                          {chain?.image_build?.repository && (
                            <div className="flex items-center gap-2">
                              <Database className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                              <span className="text-gray-500 w-16 shrink-0">Image</span>
                              <span className="font-mono text-gray-300 truncate">
                                {chain.image_build.repository}{chain.image_build.image_tag ? `:${chain.image_build.image_tag}` : ''}
                              </span>
                            </div>
                          )}
                        </div>
                      </Section>
                    )}

                    <button
                      onClick={() => { onClose(); navigate('/code-to-cloud'); }}
                      className="text-xs text-purple-300 hover:text-purple-200 inline-flex items-center gap-1.5">
                      <ExternalLink className="w-3 h-3" /> View full lineage in Code-to-Cloud
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── DETAILS TAB ── */}
          {activeTab === 'details' && <>

          {/* Severity score */}
          <Section title="Risk Score" icon={<AlertTriangle />}>
            <div className="flex items-center gap-4 p-3 bg-gray-800/40 rounded-lg border border-gray-700/30">
              <div className="flex-1">
                <div className="flex justify-between text-xs text-gray-400 mb-1.5">
                  <span>{hasEffectiveRisk ? 'Effective Risk' : 'Severity Risk'}</span>
                  <span className="font-mono">{score}/100</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${sev.bar}`} style={{ width: `${score}%` }} />
                </div>
              </div>
              <div className={`text-2xl font-bold ${score >= 80 ? 'text-red-400' : score >= 60 ? 'text-orange-400' : score >= 40 ? 'text-yellow-400' : 'text-blue-400'}`}>
                {score}
              </div>
            </div>
          </Section>

          {/* Description */}
          {finding.description && (
            <Section title="Details" icon={<FileText />}>
              <p className="text-sm text-gray-300 leading-relaxed bg-gray-800/30 rounded-lg p-3 border border-gray-700/30">
                {finding.description}
              </p>
            </Section>
          )}

          {/* Affected Resource */}
          {(finding.resource_name || finding.resource_id) && (
            <Section title="Affected Resource" icon={<Server />}>
              <div className="p-3 bg-gray-800/40 rounded-lg border border-gray-700/30 space-y-2.5">
                {[
                  { icon: <ResourceIcon />,  label: 'Name',     value: finding.resource_name ?? '—' },
                  { icon: <Hash />,          label: 'ID',       value: finding.resource_id ?? '—' },
                  { icon: <Tag />,           label: 'Type',     value: finding.resource_type?.replace(/_/g, ' ').toUpperCase() ?? '—' },
                  { icon: <Globe />,         label: 'Provider', value: `${PROVIDER_FLAGS[finding.provider ?? ''] ?? '☁️'} ${(finding.provider ?? '').toUpperCase()}` },
                  { icon: <MapPin />,        label: 'Region',   value: finding.region ?? '—' },
                ].map(({ icon, label, value }) => (
                  <div key={label} className="flex items-center gap-3 text-sm">
                    <span className="w-3.5 h-3.5 text-gray-500 shrink-0 [&>svg]:w-full [&>svg]:h-full">{icon}</span>
                    <span className="text-gray-500 w-16 shrink-0 text-xs">{label}</span>
                    <span className="text-gray-200 font-mono text-xs truncate">{value}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* CVE / Vulnerability Enrichment */}
          {((finding as any).cve_ids?.length > 0 || (finding as any).epss_score != null || (finding as any).cvss_score != null
            || (finding as any).affected_package || (finding as any).fixed_version) && (
            <Section title="Vulnerability Intelligence" icon={<Bug />}>
              <div className="space-y-3">
                {/* CVE IDs with clickable NVD links — defensive: API can ship
                    cve_ids as string ("CVE-2024-X"), array, or stringified JSON. */}
                {(() => {
                  const raw = (finding as any).cve_ids;
                  let cves: string[] = [];
                  if (Array.isArray(raw)) cves = raw.filter(Boolean).map(String);
                  else if (typeof raw === 'string' && raw.trim().startsWith('[')) {
                    try { const p = JSON.parse(raw); if (Array.isArray(p)) cves = p.filter(Boolean).map(String); } catch {}
                  } else if (typeof raw === 'string' && raw.trim()) {
                    cves = [raw.trim()];
                  }
                  if (cves.length === 0) return null;
                  return (
                    <div className="flex flex-wrap gap-2">
                      {cves.map((cve: string) => (
                        <a key={cve} href={`https://nvd.nist.gov/vuln/detail/${cve}`} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-red-500/10 text-red-300 border border-red-500/20 text-xs font-mono hover:bg-red-500/20 transition-colors">
                          <Bug className="w-3 h-3" />{cve}
                          <span className="text-xs text-red-400/60">NVD →</span>
                        </a>
                      ))}
                    </div>
                  );
                })()}
                {/* EPSS + CVSS scores */}
                <div className="grid grid-cols-2 gap-3">
                  {(finding as any).epss_score != null && (
                    <div className="p-3 bg-orange-500/5 rounded-lg border border-orange-500/20">
                      <div className="text-xs text-orange-400 font-semibold uppercase mb-1">EPSS Score</div>
                      <div className="text-lg font-bold text-orange-400">{((finding as any).epss_score * 100).toFixed(1)}%</div>
                      <div className="text-xs text-gray-500">Exploit probability (30 days)</div>
                    </div>
                  )}
                  {(finding as any).cvss_score != null && (
                    <div className="p-3 bg-red-500/5 rounded-lg border border-red-500/20">
                      <div className="text-xs text-red-400 font-semibold uppercase mb-1">CVSS Score</div>
                      <div className="text-lg font-bold text-red-400">{(finding as any).cvss_score}</div>
                      <div className="text-xs text-gray-500">Base severity score</div>
                    </div>
                  )}
                </div>
                {/* Affected package / installed / fixed version — CVE-specific
                    fields many vuln findings carry. Rendering them here lets the
                    universal drawer fully replace pillar CVE row-expanders. */}
                {((finding as any).affected_package || (finding as any).affected_version || (finding as any).fixed_version) && (
                  <div className="grid grid-cols-3 gap-2">
                    {(finding as any).affected_package && (
                      <div className="p-2.5 bg-black/20 rounded-lg border border-gray-800 text-xs">
                        <div className="text-xs text-gray-500 uppercase mb-0.5">Package</div>
                        <div className="text-gray-200 font-mono truncate" title={(finding as any).affected_package}>{(finding as any).affected_package}</div>
                      </div>
                    )}
                    {(finding as any).affected_version && (
                      <div className="p-2.5 bg-black/20 rounded-lg border border-gray-800 text-xs">
                        <div className="text-xs text-gray-500 uppercase mb-0.5">Installed</div>
                        <div className="text-gray-200 font-mono truncate" title={(finding as any).affected_version}>{(finding as any).affected_version}</div>
                      </div>
                    )}
                    {(finding as any).fixed_version ? (
                      <div className="p-2.5 bg-green-500/5 rounded-lg border border-green-500/20 text-xs">
                        <div className="text-xs text-green-400 uppercase mb-0.5">Fixed in</div>
                        <div className="text-green-300 font-mono truncate" title={(finding as any).fixed_version}>{(finding as any).fixed_version}</div>
                      </div>
                    ) : (finding as any).affected_package ? (
                      <div className="p-2.5 bg-black/20 rounded-lg border border-gray-800 text-xs">
                        <div className="text-xs text-gray-500 uppercase mb-0.5">Fix</div>
                        <div className="text-gray-500">No fix available</div>
                      </div>
                    ) : null}
                  </div>
                )}
                {/* Reachability */}
                {(finding as any).is_reachable && (
                  <div className="flex items-center gap-2 p-2.5 bg-red-500/10 rounded-lg border border-red-500/20">
                    <Globe className="w-4 h-4 text-red-400" />
                    <span className="text-xs text-red-300 font-medium">Internet Reachable — vulnerable process is directly accessible</span>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Trend + Scan Method */}
          {((finding as any).trend || (finding as any).scan_method) && (
            <Section title="Detection Context" icon={<Activity />}>
              <div className="flex flex-wrap gap-2">
                {(finding as any).trend === 'new' && <span className="px-2.5 py-1 rounded-lg bg-green-500/15 text-green-400 border border-green-500/25 text-xs font-semibold">🆕 New Finding</span>}
                {(finding as any).trend === 'recurring' && <span className="px-2.5 py-1 rounded-lg bg-yellow-500/15 text-yellow-400 border border-yellow-500/25 text-xs font-semibold">🔄 Recurring</span>}
                {(finding as any).trend === 'persistent' && <span className="px-2.5 py-1 rounded-lg bg-red-500/15 text-red-400 border border-red-500/25 text-xs font-semibold">⚠️ Persistent (30d+)</span>}
                {(finding as any).scan_method && <span className="px-2.5 py-1 rounded-lg bg-blue-500/15 text-blue-400 border border-blue-500/25 text-xs font-medium">📡 {(finding as any).scan_method}</span>}
                {(finding as any).blast_radius > 0 && <span className="px-2.5 py-1 rounded-lg bg-purple-500/15 text-purple-400 border border-purple-500/25 text-xs font-medium">💥 Blast Radius: {(finding as any).blast_radius}</span>}
              </div>
            </Section>
          )}

          {/* Risk Signal Breakdown — Wiz-parity "why is this N/100?"
              Decomposes the signal-fused effective_risk score so users can
              see which amplifiers fired. Values mirror the formula in
              finding-normalizer._compute_effective_risk. */}
          {typeof finding.effective_risk === 'number' && finding.effective_risk > 0 && (
            <Section
              title="Risk Signal Breakdown"
              icon={<Zap />}
              tag={`${finding.effective_risk.toFixed(0)} / 100`}
            >
              <p className="text-[11px] text-gray-500 mb-3">
                Signal-fused score: severity base × exposure multipliers + blast contribution.
                Multipliers highlighted below add to the base severity score.
              </p>

              {/* Base severity anchor */}
              <div className="mb-3 px-3 py-2 bg-black/20 border border-gray-800 rounded text-xs flex items-center justify-between">
                <span className="text-gray-400">Severity base</span>
                <span className="font-mono text-gray-200">
                  {finding.severity?.toUpperCase() || '?'}
                  <span className="ml-2 text-purple-300">
                    {SEV_SCORE[finding.severity?.toLowerCase() || 'medium'] ?? 45}
                  </span>
                </span>
              </div>

              {/* Amplifier grid — each chip dims when the signal is false */}
              <div className="grid grid-cols-2 gap-1.5">
                {([
                  ['is_internet_facing',               '+25%', 'Internet-facing',                    Globe],
                  ['has_sensitive_data',               '+20%', 'Sensitive data nearby',              Database],
                  ['has_admin_access',                 '+15%', 'Admin-level access',                 Key],
                  ['exploit_available',                '+25%', 'Public exploit available',           Bug],
                  ['privilege_escalation_possible',    '+15%', 'Privilege escalation',               AlertTriangle],
                  ['lateral_movement_possible',        '+15%', 'Lateral movement',                   Network],
                ] as const).map(([field, bump, label, Icon]) => {
                  const fired = Boolean((finding as any)[field]);
                  return (
                    <div
                      key={field}
                      className={`px-2.5 py-1.5 rounded border text-[11px] flex items-center gap-2 ${
                        fired
                          ? 'bg-orange-500/10 border-orange-500/30 text-orange-300'
                          : 'bg-gray-500/5 border-gray-800 text-gray-600'
                      }`}
                    >
                      <Icon className="w-3 h-3 shrink-0" />
                      <span className="flex-1 truncate">{label}</span>
                      <span className="font-mono shrink-0">
                        {fired ? bump : '—'}
                      </span>
                    </div>
                  );
                })}
                {/* EPSS & blast-radius aren't booleans — show as thresholds */}
                {typeof finding.epss_score === 'number' && (
                  <div
                    className={`px-2.5 py-1.5 rounded border text-[11px] flex items-center gap-2 col-span-2 ${
                      finding.epss_score >= 0.5
                        ? 'bg-orange-500/10 border-orange-500/30 text-orange-300'
                        : 'bg-gray-500/5 border-gray-800 text-gray-500'
                    }`}
                  >
                    <AlertCircle className="w-3 h-3 shrink-0" />
                    <span className="flex-1">EPSS = {(finding.epss_score * 100).toFixed(1)}%</span>
                    <span className="font-mono shrink-0">
                      {finding.epss_score >= 0.5 ? '+20%' : '—'}
                    </span>
                  </div>
                )}
                {typeof finding.blast_radius_score === 'number' && finding.blast_radius_score > 0 && (
                  <div className="px-2.5 py-1.5 rounded border bg-purple-500/10 border-purple-500/30 text-purple-300 text-[11px] flex items-center gap-2 col-span-2">
                    <Activity className="w-3 h-3 shrink-0" />
                    <span className="flex-1">
                      Blast radius: {finding.blast_radius_score.toFixed(0)} (reaches N downstream resources)
                    </span>
                    <span className="font-mono shrink-0">
                      +{Math.min(30, finding.blast_radius_score * 0.3).toFixed(0)} pts
                    </span>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Compliance Controls (per-finding). Defensive normalize: API may
              ship this as an array, a JSON-stringified array, or an object —
              pre-fix any non-array value crashed FindingDetail with
              "controls.map is not a function" and the panel never opened. */}
          {(() => {
            const raw = (finding as any).compliance_controls;
            let controls: string[] = [];
            if (Array.isArray(raw)) {
              controls = raw.filter(Boolean).map(String);
            } else if (typeof raw === 'string' && raw.trim().startsWith('[')) {
              try { const p = JSON.parse(raw); if (Array.isArray(p)) controls = p.filter(Boolean).map(String); } catch {}
            }
            if (controls.length === 0) return null;
            return (
              <Section title="Compliance Controls" icon={<Shield />}>
                <div className="flex flex-wrap gap-1.5">
                  {controls.map((ctrl: string) => {
                    const c = ctrl.toLowerCase();
                    const style = c.includes('cis') ? 'bg-blue-500/10 text-blue-300 border-blue-500/20'
                      : c.includes('nist') ? 'bg-green-500/10 text-green-300 border-green-500/20'
                      : c.includes('pci') ? 'bg-red-500/10 text-red-300 border-red-500/20'
                      : c.includes('soc') ? 'bg-orange-500/10 text-orange-300 border-orange-500/20'
                      : c.includes('hipaa') ? 'bg-pink-500/10 text-pink-300 border-pink-500/20'
                      : 'bg-gray-500/10 text-gray-300 border-gray-500/20';
                    return <span key={ctrl} className={`text-xs px-2 py-0.5 rounded border font-mono ${style}`}>{ctrl}</span>;
                  })}
                </div>
              </Section>
            );
          })()}

          {/* Wiz-parity MITRE ATT&CK tags — server populates these via
              mig 213 trg_enrich_finding from rule metadata. Tactic + technique
              shown as red pills so analysts can pivot from a finding to
              defensive coverage at a glance. */}
          {(() => {
            const norm = (v: any): string[] => {
              if (!v) return [];
              if (Array.isArray(v)) return v.map(String).filter(Boolean);
              if (typeof v === 'string') {
                try {
                  const parsed = JSON.parse(v);
                  return Array.isArray(parsed) ? parsed.map(String) : [v];
                } catch { return v.split(',').map(s => s.trim()).filter(Boolean); }
              }
              return [];
            };
            const tactics = norm(finding.mitre_tactics);
            const techniques = norm(finding.mitre_techniques);
            if (tactics.length === 0 && techniques.length === 0) return null;
            return (
              <Section title="MITRE ATT&CK" icon={<AlertTriangle />}>
                <div className="space-y-2">
                  {tactics.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Tactics</div>
                      <div className="flex flex-wrap gap-1.5">
                        {tactics.map(t => (
                          <span key={t} className="text-xs px-2 py-0.5 rounded bg-red-500/10 text-red-300 border border-red-500/30 font-mono">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {techniques.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Techniques</div>
                      <div className="flex flex-wrap gap-1.5">
                        {techniques.map(t => (
                          <span key={t} className="text-xs px-2 py-0.5 rounded bg-orange-500/10 text-orange-300 border border-orange-500/30 font-mono">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Section>
            );
          })()}

          {/* Compliance Frameworks. Defensive normalize: API may ship as
              array-of-strings (`["CIS","SOC2"]`), object-of-arrays
              (`{"CIS":["1.5"]}`), or stringified JSON. Pre-fix the
              `(controls as string[]).map` crashed when API delivered the
              array shape — Object.entries(array) yields ["0", string]
              entries and string.map is not a function. */}
          {(() => {
            const raw: any = (finding as any).compliance_frameworks;
            let pairs: [string, string[]][] = [];
            const parse = (v: any): any => {
              if (typeof v === 'string' && v.trim().startsWith('[') || (typeof v === 'string' && v.trim().startsWith('{'))) {
                try { return JSON.parse(v); } catch { return v; }
              }
              return v;
            };
            const data = parse(raw);
            if (Array.isArray(data)) {
              // Array of framework names — render under a single "Frameworks" group
              const frameworks = data.filter(Boolean).map(String);
              if (frameworks.length === 0) return null;
              pairs = [['Frameworks', frameworks]];
            } else if (data && typeof data === 'object') {
              for (const [fw, ctrls] of Object.entries(data)) {
                let controls: string[] = [];
                if (Array.isArray(ctrls)) controls = ctrls.filter(Boolean).map(String);
                else if (typeof ctrls === 'string') controls = [ctrls];
                pairs.push([fw, controls]);
              }
            }
            if (pairs.length === 0) return null;
            return (
              <Section title="Compliance Frameworks" icon={<CheckCircle2 />}>
                <div className="space-y-2">
                  {pairs.map(([framework, controls]) => (
                    <div key={framework} className="p-3 bg-gray-800/40 rounded-lg border border-gray-700/30">
                      <div className="text-xs font-semibold text-indigo-400 mb-1.5">{framework}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {controls.map((ctrl: string) => (
                          <span key={ctrl} className="text-xs px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">
                            {ctrl}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            );
          })()}

          {/* Timeline */}
          <Section title="Timeline" icon={<Calendar />}>
            <div className="space-y-0 text-sm">
              {[
                { label: 'Detected',   value: fmtDate(finding.detected_at) },
                { label: 'Finding ID', value: finding.id },
                { label: 'Module',     value: finding.source_module.replace(/_/g, '-').toUpperCase() },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between py-2 border-b border-gray-800/60 last:border-0">
                  <span className="text-gray-500 text-xs">{label}</span>
                  <span className="text-gray-300 font-mono text-xs">{value}</span>
                </div>
              ))}
            </div>
          </Section>

          </> /* end details tab */}

          {/* ── RELATED TAB — fingerprint-grouped cross-scanner siblings ── */}
          {activeTab === 'related' && (
            <Section
              title="Related Findings"
              icon={<Hash />}
              tag={related?.detected_by_count
                ? `${related.detected_by_count} scanner${related.detected_by_count === 1 ? '' : 's'}`
                : undefined}
            >
              <p className="text-xs text-gray-500 mb-3">
                Findings sharing the same fingerprint — same (rule, resource, CVE)
                detected by different scanners. Groups agentless + runtime + container
                scans of the same root issue.
              </p>

              {related?.loading && (
                <div className="text-xs text-gray-500">Loading siblings…</div>
              )}
              {related?.error && <TabError error={related.error} />}

              {related?.related !== undefined && !related.loading && !related.error && (
                related.related.length === 0 ? (
                  <div className="text-xs text-gray-500">
                    No other scanners detected this finding. Either it's unique to
                    this scanner, or the fingerprint isn't populated yet (legacy
                    pre-fingerprint finding).
                  </div>
                ) : (
                  <>
                    <div className="text-[11px] text-gray-400 mb-2 font-mono">
                      fingerprint: {related.fingerprint?.slice(0, 16)}…
                    </div>
                    <div className="space-y-2">
                      {related.related.map(r => (
                        <div
                          key={r.id}
                          className="px-3 py-2 border border-gray-800 rounded bg-black/20 text-xs flex items-start gap-2"
                        >
                          <span
                            className={`shrink-0 px-1.5 py-0.5 rounded text-xs font-mono uppercase ${
                              MODULE_COLORS[r.source_module] ||
                              'bg-gray-500/15 text-gray-300 border-gray-500/25'
                            }`}
                          >
                            {r.source_module}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-gray-200 truncate">{r.title}</div>
                            <div className="text-gray-500 mt-0.5">
                              <span
                                className={`inline-block px-1.5 rounded text-xs ${
                                  SEV_STYLES[(r.severity || 'low').toLowerCase()] || ''
                                }`}
                              >
                                {r.severity}
                              </span>
                              <span className="mx-1.5">·</span>
                              <span>{r.status}</span>
                              {typeof r.effective_risk === 'number' && (
                                <>
                                  <span className="mx-1.5">·</span>
                                  <span className="text-purple-300">risk {r.effective_risk.toFixed(0)}</span>
                                </>
                              )}
                              <span className="mx-1.5">·</span>
                              <span>{timeAgo(r.first_seen_at)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )
              )}
            </Section>
          )}

          {/* ── HISTORY TAB — change log backed by csp_core.finding_history ── */}
          {activeTab === 'history' && (
            <Section
              title="Change History"
              icon={<Clock />}
              tag={history?.events ? `${history.events.length} event${history.events.length === 1 ? '' : 's'}` : undefined}
            >
              <p className="text-xs text-gray-500 mb-3">
                Only meaningful changes land here: status flip, severity flip, or ≥10pt effective_risk swing.
                Routine re-scans (bumping last_seen_at only) don't create rows.
              </p>

              {history?.loading && (
                <div className="text-xs text-gray-500">Loading history…</div>
              )}
              {history?.error && <TabError error={history.error} />}

              {history?.events !== undefined && !history.loading && !history.error && (
                history.events.length === 0 ? (
                  <div className="text-xs text-gray-500">
                    No changes recorded yet — this finding hasn't flipped status or severity
                    since creation. Every re-scan that doesn't change anything is intentionally
                    suppressed so the log stays readable.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                    {history.events.map(ev => (
                      <div
                        key={ev.id}
                        className="px-3 py-2 border border-gray-800 rounded bg-black/20 text-xs flex items-start gap-2"
                      >
                        <span
                          className={`shrink-0 px-1.5 py-0.5 rounded text-xs font-mono uppercase ${
                            ev.change_type === 'created'    ? 'bg-green-500/15 text-green-300' :
                            ev.change_type === 'resolved'   ? 'bg-purple-500/15 text-purple-300' :
                            ev.change_type === 'reopened'   ? 'bg-yellow-500/15 text-yellow-300' :
                            ev.change_type === 'suppressed' ? 'bg-orange-500/15 text-orange-300' :
                            ev.change_type === 'updated'    ? 'bg-blue-500/15 text-blue-300' :
                            'bg-gray-500/15 text-gray-300'
                          }`}
                        >
                          {ev.change_type}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-200">
                            <span className="text-gray-400">by</span> {ev.changed_by || 'system'}
                            <span className="mx-1.5 text-gray-500">·</span>
                            <span className="text-gray-400">
                              {ev.changed_at ? new Date(ev.changed_at).toLocaleString() : '—'}
                            </span>
                          </div>
                          {/* Show the transition if values actually differ. */}
                          {(ev.prev_status || ev.new_status) && ev.prev_status !== ev.new_status && (
                            <div className="text-gray-500 mt-0.5">
                              status: <span className="text-gray-400">{ev.prev_status || '—'}</span>
                              {' → '}
                              <span className="text-gray-300">{ev.new_status || '—'}</span>
                            </div>
                          )}
                          {(ev.prev_severity || ev.new_severity) && ev.prev_severity !== ev.new_severity && (
                            <div className="text-gray-500 mt-0.5">
                              severity: <span className="text-gray-400">{ev.prev_severity || '—'}</span>
                              {' → '}
                              <span className="text-gray-300">{ev.new_severity || '—'}</span>
                            </div>
                          )}
                          {typeof ev.new_effective_risk === 'number'
                            && typeof ev.prev_effective_risk === 'number'
                            && Math.abs(ev.new_effective_risk - ev.prev_effective_risk) >= 0.5 && (
                            <div className="text-gray-500 mt-0.5">
                              risk: <span className="text-gray-400">{ev.prev_effective_risk.toFixed(0)}</span>
                              {' → '}
                              <span className="text-purple-300">{ev.new_effective_risk.toFixed(0)}</span>
                            </div>
                          )}
                          {ev.note && (
                            <div className="text-gray-500 mt-0.5 italic truncate" title={ev.note}>
                              {ev.note}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </Section>
          )}

          {/* ── REMEDIATION TAB ── */}
          {activeTab === 'remediation' && (() => {
            // Preference order for the steps shown:
            //  1. backend rule/resource-specific steps (canonical findings)
            //  2. remediation text carried on the finding row itself — many
            //     pillar-local findings (KSPM, SSPM, …) ship a `remediation`
            //     string even though they aren't in the unified findings store
            //  3. the client-side heuristic derived from the finding type
            const backendSteps = normalizeBackendSteps(remediationData?.data?.steps);
            const rowSteps = normalizeBackendSteps(
              (finding as any).remediation ?? (finding as any).remediation_steps
            );
            const usingBackend = backendSteps.length > 0;
            const usingRow = !usingBackend && rowSteps.length > 0;
            const shownSteps = usingBackend ? backendSteps : usingRow ? rowSteps : steps;
            const isGeneric = !usingBackend && !usingRow;
            const description = remediationData?.data?.description;
            return (
            <div data-testid="remediation-section">
              {remediationData?.loading && (
                <div className="text-xs text-gray-500 px-1 mb-3">Loading remediation guidance…</div>
              )}
              {description && (
                <p className="text-sm text-gray-300 leading-relaxed mb-4 px-1">{description}</p>
              )}

              {/* ── Loop-closing action bar ──────────────────────────────────
                  Auto-Remediate appears only when the backend reported the
                  rule as auto_remediable; Create Ticket is always available.
                  Both show the real backend outcome (no faked success). */}
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {remediationData?.data?.auto_remediable && (
                  <button
                    data-testid="remediate-action"
                    onClick={handleRemediate}
                    disabled={remediating}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                      bg-wiz-purple/20 hover:bg-wiz-purple/30 text-wiz-purple border border-wiz-purple/30
                      transition-colors disabled:opacity-50"
                  >
                    {remediating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
                    {remediating ? 'Requesting…' : 'Auto-Remediate'}
                  </button>
                )}
                <button
                  data-testid="create-ticket-action"
                  onClick={handleCreateTicket}
                  disabled={ticketing}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                    bg-gray-800 hover:bg-gray-700 text-gray-200 border border-gray-700
                    transition-colors disabled:opacity-50"
                >
                  {ticketing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />}
                  {ticketing ? 'Creating…' : 'Create Ticket'}
                </button>
              </div>

              {remediateResult && (
                <div
                  className={`mb-3 px-3 py-2 rounded-lg text-xs border ${
                    remediateResult.ok
                      ? 'bg-green-500/10 text-green-300 border-green-500/25'
                      : 'bg-yellow-500/10 text-yellow-300 border-yellow-500/25'
                  }`}
                >
                  {remediateResult.message}
                </div>
              )}
              {ticketResult && (
                <div
                  className={`mb-3 px-3 py-2 rounded-lg text-xs border flex items-center gap-2 ${
                    ticketResult.ok
                      ? 'bg-green-500/10 text-green-300 border-green-500/25'
                      : 'bg-red-500/10 text-red-300 border-red-500/25'
                  }`}
                >
                  <span>{ticketResult.message}</span>
                  {ticketResult.url && (
                    <a href={ticketResult.url} target="_blank" rel="noopener noreferrer" className="underline">
                      open →
                    </a>
                  )}
                </div>
              )}
              <Section
                title="Remediation Steps"
                icon={<Shield />}
                tag={isGeneric ? 'Suggested' : undefined}
              >
                {isGeneric && (
                  <p className="text-[11px] text-gray-500 mb-2">
                    Generic guidance derived from the finding type. Rule-specific
                    steps appear here once the backend provides them.
                  </p>
                )}
                <ol className="space-y-3" data-testid="remediation-steps">
                  {shownSteps.map((step, i) => (
                    <li key={i} data-testid="remediation-step" className="flex gap-3 text-sm text-gray-300">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-500/20 text-purple-300 text-xs flex items-center justify-center font-bold mt-0.5">
                        {i + 1}
                      </span>
                      <span className="leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ol>
              </Section>

              {/* M1 / #6 — IaC fix code. Sourced from the rule catalog when it
                  carries Terraform, else from the remediation-service generator
                  (backend reports which via iac_source). Copy-to-clipboard on
                  each block + "Open as PR" wires the real fix-in-code flow. */}
              {remediationData?.data?.terraform ? (
                <Section
                  title="Infrastructure-as-Code Fix"
                  icon={<FileText />}
                  tag="Terraform"
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-[11px] text-gray-500">
                      Copy-paste block for your Terraform config. Customize
                      commented values (e.g. CIDR ranges) before applying.
                      {remediationData.data.iac_source === 'generator' && (
                        <span className="ml-1 text-gray-600">(generated from fix template)</span>
                      )}
                    </p>
                    <CopyButton text={remediationData.data.terraform} label="Copy HCL" />
                  </div>
                  <pre className="text-[11px] font-mono text-gray-200 bg-black/40 border border-wiz-border rounded-lg p-3 overflow-x-auto whitespace-pre">
                    {remediationData.data.terraform}
                  </pre>
                  {remediationData.data.cli && (
                    <>
                      <div className="flex items-center justify-between gap-2 mt-3 mb-1">
                        <div className="text-[11px] text-gray-400">Or via CLI:</div>
                        <CopyButton text={remediationData.data.cli} label="Copy CLI" />
                      </div>
                      <pre className="text-[11px] font-mono text-gray-200 bg-black/40 border border-wiz-border rounded-lg p-3 overflow-x-auto whitespace-pre">
                        {remediationData.data.cli}
                      </pre>
                    </>
                  )}

                  {/* Open as PR — creates a real GitHub PR with this fix. */}
                  <div className="mt-3 pt-3 border-t border-wiz-border/50">
                    {prState?.url ? (
                      <a
                        href={prState.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/30 text-xs text-green-400 hover:bg-green-500/20"
                      >
                        <GitPullRequest className="w-3.5 h-3.5" />
                        PR #{prState.number} opened — view on GitHub
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : !prForm.open ? (
                      <button
                        onClick={() => { setPrState(null); setPrForm(f => ({ ...f, open: true })); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-wiz-purple/10 border border-wiz-purple/30 text-xs text-wiz-purple hover:bg-wiz-purple/20"
                      >
                        <GitPullRequest className="w-3.5 h-3.5" />
                        Open as PR
                      </button>
                    ) : (
                      <div className="space-y-2 bg-black/20 border border-wiz-border rounded-lg p-3">
                        <p className="text-[11px] text-gray-400">
                          Opens a real pull request with this fix in your repo. The
                          token is used once for this request and not stored.
                        </p>
                        <input
                          value={prForm.repo_url}
                          onChange={e => setPrForm(f => ({ ...f, repo_url: e.target.value }))}
                          placeholder="Repository URL (e.g. https://github.com/org/repo)"
                          className="w-full px-2.5 py-1.5 bg-gray-900 border border-gray-700 rounded text-xs text-gray-200 placeholder-gray-600 outline-none"
                        />
                        <div className="flex gap-2">
                          <input
                            value={prForm.branch}
                            onChange={e => setPrForm(f => ({ ...f, branch: e.target.value }))}
                            placeholder="Base branch"
                            className="flex-1 px-2.5 py-1.5 bg-gray-900 border border-gray-700 rounded text-xs text-gray-200 placeholder-gray-600 outline-none"
                          />
                          <select
                            value={prForm.format}
                            onChange={e => setPrForm(f => ({ ...f, format: e.target.value }))}
                            className="px-2.5 py-1.5 bg-gray-900 border border-gray-700 rounded text-xs text-gray-200 outline-none"
                          >
                            <option value="terraform">terraform</option>
                            <option value="cloudformation">cloudformation</option>
                            <option value="pulumi">pulumi</option>
                          </select>
                        </div>
                        <input
                          type="password"
                          value={prForm.token}
                          onChange={e => setPrForm(f => ({ ...f, token: e.target.value }))}
                          placeholder="GitHub token (repo scope)"
                          className="w-full px-2.5 py-1.5 bg-gray-900 border border-gray-700 rounded text-xs text-gray-200 placeholder-gray-600 outline-none"
                        />
                        {prState?.error && <p className="text-[11px] text-red-400">{prState.error}</p>}
                        <div className="flex gap-2">
                          <button
                            onClick={handleOpenPr}
                            disabled={!prForm.repo_url.trim() || !prForm.token.trim() || prState?.loading}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-wiz-purple text-white text-xs disabled:opacity-50"
                          >
                            {prState?.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitPullRequest className="w-3.5 h-3.5" />}
                            Create PR
                          </button>
                          <button
                            onClick={() => setPrForm(f => ({ ...f, open: false, token: '' }))}
                            className="px-3 py-1.5 rounded-lg border border-wiz-border text-gray-400 text-xs hover:text-white"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </Section>
              ) : null}
            </div>
            );
          })()}

          {/* ── NOTES TAB ── */}
          {activeTab === 'notes' && (
            <Section title="Notes" icon={<MessageSquare />}>
              {notes.length > 0 && (
                <div className="space-y-2 mb-3">
                  {notes.map((n: any, i: number) => (
                    <div key={n.id || i} className="p-2 bg-gray-800/40 rounded-lg border border-gray-700/30 text-sm">
                      <p className="text-gray-300">{n.content || n.text || n.note}</p>
                      <p className="text-xs text-gray-600 mt-1">{n.author || n.by || 'User'} &middot; {(n.created_at || n.at) ? fmtDateTime(n.created_at || n.at) : ''}</p>
                    </div>
                  ))}
                </div>
              )}
              {noteError && (
                <p className="text-xs text-red-400 mb-2">{noteError}</p>
              )}
              <div className="flex gap-2">
                <input type="text" value={newNote} onChange={e => setNewNote(e.target.value)}
                  placeholder="Add a note..."
                  onKeyDown={e => { if (e.key === 'Enter') handleAddNote(); }}
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-600 outline-none" />
                <button onClick={handleAddNote} disabled={!newNote.trim()}
                  className="px-3 py-2 bg-purple-500/20 text-purple-300 border border-purple-500/30 rounded-lg text-sm hover:bg-purple-500/30 disabled:opacity-50 transition-colors">
                  Add
                </button>
              </div>
            </Section>
          )}

        </div>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        {resolveError && (
          <div className="flex-shrink-0 px-4 pt-2 text-xs text-red-400 bg-wiz-darker">{resolveError}</div>
        )}
        <div className="flex-shrink-0 border-t border-gray-800 p-4 flex items-center gap-3 bg-wiz-darker">
          <button
            onClick={handleResolve}
            disabled={resolving}
            className="flex-1 px-4 py-2 bg-wiz-purple/20 hover:bg-wiz-purple/30 text-wiz-purple border border-wiz-purple/30 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
            <CheckCircle2 className="w-4 h-4" />
            {resolving ? 'Resolving...' : 'Mark Resolved'}
          </button>
          <button
            onClick={() => { onClose(); navigate(`/${(finding.source_module || '').replace(/_/g, '-')}`); }}
            className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 border border-gray-700 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2">
            <Eye className="w-4 h-4" />
            View in {finding.source_module.toUpperCase()}
          </button>
          <button
            onClick={handleSuppress}
            disabled={suppressing}
            className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 border border-gray-700 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
            <XCircle className="w-4 h-4" />
            {suppressing ? 'Suppressing...' : 'Suppress'}
          </button>
        </div>
      </div>
    </>
  );
};

export default FindingDetail;
